import { useEffect, useMemo, useState } from "react";
import { getClients, getEvents, getMe, getNodes, getPartners } from "./lib/api";
import type { ClientRecord, CrmEvent, CurrentUser, NodeRecord, PartnerRecord, Permissions, WarmEntity, WarmEntityType } from "./lib/types";
import { sampleClients, sampleEvents, sampleNodes, samplePartners, samplePermissions, sampleUser } from "./lib/sampleData";
import { Sidebar } from "./components/Sidebar";
import type { CrmView } from "./components/Sidebar";
import { Topbar } from "./components/Topbar";
import { Login } from "./components/Login";
import {
  AssignmentsPage,
  ClientsPage,
  EventsPage,
  FollowUpsPage,
  NodesPage,
  OverviewPage,
  PartnersPage,
  PerformancePage,
  ProofPage,
  PulsePage,
  ReportsPage,
  SettingsPage
} from "./components/Pages";
import { LeadDetail } from "./components/LeadDetail";

export default function App() {
  const [authenticated, setAuthenticated] = useState(import.meta.env.DEV);
  const [user, setUser] = useState<CurrentUser | null>(import.meta.env.DEV ? sampleUser : null);
  const [permissions, setPermissions] = useState<Permissions | null>(import.meta.env.DEV ? samplePermissions : null);
  const [clients, setClients] = useState<ClientRecord[]>(import.meta.env.DEV ? sampleClients : []);
  const [partners, setPartners] = useState<PartnerRecord[]>(import.meta.env.DEV ? samplePartners : []);
  const [nodes, setNodes] = useState<NodeRecord[]>(import.meta.env.DEV ? sampleNodes : []);
  const [events, setEvents] = useState<CrmEvent[]>(import.meta.env.DEV ? sampleEvents : []);
  const [selected, setSelected] = useState<{ type: WarmEntityType; entity: WarmEntity } | null>(import.meta.env.DEV ? { type: "client", entity: sampleClients[0] } : null);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState("");
  const [activeView, setActiveView] = useState<CrmView>("overview");

  useEffect(() => {
    if (authenticated || import.meta.env.DEV) return;
    getMe()
      .then((data) => {
        setUser(data.user);
        setPermissions(data.permissions);
        setAuthenticated(true);
      })
      .catch(() => setAuthenticated(false));
  }, [authenticated]);

  useEffect(() => {
    if (!authenticated) return;
    refreshWorkspace().catch(() => {
      if (!import.meta.env.DEV) setAuthenticated(false);
    });
  }, [authenticated]);

  const selectedId = selected?.entity.id;
  const visibleCounts = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return { clients: clients.length, partners: partners.length, nodes: nodes.length };
    return {
      clients: clients.filter((client) => entitySearch(client).includes(q)).length,
      partners: partners.filter((partner) => entitySearch(partner).includes(q)).length,
      nodes: nodes.filter((node) => entitySearch(node).includes(q)).length
    };
  }, [clients, partners, nodes, query]);

  async function refreshWorkspace() {
    setLoading(true);
    try {
      const [me, nextClients, nextPartners, nextNodes, nextEvents] = await Promise.all([getMe(), getClients(), getPartners(), getNodes(), getEvents()]);
      setUser(me.user);
      setPermissions(me.permissions);
      setClients(nextClients);
      setPartners(nextPartners);
      setNodes(nextNodes);
      setEvents(nextEvents);
      if (!selected && nextClients[0]) setSelected({ type: "client", entity: nextClients[0] });
    } finally {
      setLoading(false);
    }
  }

  function selectEntity(type: WarmEntityType, entity: WarmEntity) {
    setSelected({ type, entity });
  }

  function handleSearch(value: string) {
    setQuery(value);
    const q = value.trim().toLowerCase();
    if (!q) return;
    const match =
      clients.find((client) => entitySearch(client).includes(q)) ||
      partners.find((partner) => entitySearch(partner).includes(q)) ||
      nodes.find((node) => entitySearch(node).includes(q));
    if (match) {
      const type: WarmEntityType = "display_name" in match ? "client" : "node_type" in match ? "node" : "partner";
      setSelected({ type, entity: match });
    }
  }

  if (!authenticated) {
    return (
      <Login
        onLogin={async () => {
          const data = await getMe();
          setUser(data.user);
          setPermissions(data.permissions);
          setAuthenticated(true);
        }}
      />
    );
  }

  return (
    <div className="app-shell">
      <Sidebar activeView={activeView} onChangeView={setActiveView} user={user} />
      <main className="workspace">
        <Topbar
          query={query}
          onQuery={setQuery}
          onSearch={handleSearch}
          loading={loading}
          user={user}
          notice={notice || `${visibleCounts.clients} clients, ${visibleCounts.partners} partners, ${visibleCounts.nodes} nodes`}
          onRefresh={() => refreshWorkspace().catch((error) => setNotice(error instanceof Error ? error.message : "Refresh failed."))}
        />

        {activeView === "overview" ? <OverviewPage clients={clients} partners={partners} nodes={nodes} events={events} onSelect={selectEntity} /> : null}
        {activeView === "followups" ? (
          <FollowUpsPage clients={clients} selectedId={selectedId} onSelect={(client) => selectEntity("client", client)} />
        ) : null}
        {activeView === "clients" ? (
          <ClientsPage
            clients={clients}
            partners={partners}
            nodes={nodes}
            query={query}
            permissions={permissions}
            selectedId={selectedId}
            onSelect={(client) => selectEntity("client", client)}
            onCreated={(client) => {
              setClients((current) => [client, ...current]);
              selectEntity("client", client);
              setNotice("Warm lead added.");
            }}
          />
        ) : null}
        {activeView === "partners" ? (
          <PartnersPage
            partners={partners}
            query={query}
            selectedId={selectedId}
            onSelect={(partner) => selectEntity("partner", partner)}
            onCreated={(partner) => {
              setPartners((current) => [partner, ...current]);
              selectEntity("partner", partner);
              setNotice("Partner candidate added.");
            }}
          />
        ) : null}
        {activeView === "nodes" ? (
          <NodesPage
            nodes={nodes}
            query={query}
            selectedId={selectedId}
            onSelect={(node) => selectEntity("node", node)}
            onCreated={(node) => {
              setNodes((current) => [node, ...current]);
              selectEntity("node", node);
              setNotice("Node candidate added.");
            }}
          />
        ) : null}
        {activeView === "assignments" ? <AssignmentsPage clients={clients} partners={partners} nodes={nodes} /> : null}
        {activeView === "events" ? <EventsPage events={events} /> : null}
        {activeView === "reports" ? <ReportsPage clients={clients} permissions={permissions} onNotice={setNotice} /> : null}
        {activeView === "performance" ? <PerformancePage clients={clients} permissions={permissions} onNotice={setNotice} /> : null}
        {activeView === "proof" ? <ProofPage clients={clients} permissions={permissions} onNotice={setNotice} /> : null}
        {activeView === "pulse" ? <PulsePage clients={clients} permissions={permissions} onNotice={setNotice} /> : null}
        {activeView === "settings" ? <SettingsPage permissions={permissions} onNotice={setNotice} /> : null}
      </main>
      <LeadDetail selected={selected} permissions={permissions} onUpdated={refreshWorkspace} />
    </div>
  );
}

function entitySearch(entity: WarmEntity) {
  return Object.values(entity)
    .filter((value) => typeof value === "string" || typeof value === "number")
    .join(" ")
    .toLowerCase();
}
